/** @format */

import React, { useState, useMemo } from "react";
import defaultImage from "../../../assets/images/default.jpg";
import InitialsAvatar from "../../extra/InitialsAvatar";

const SellerRequestDetail = ({ data, onClose, onApprove, onReject }) => {
	const [lightboxImg, setLightboxImg] = useState(null);

	const docItems = useMemo(
		() => [
			{ label: "Government ID", url: data?.govId },
			{ label: "Registration Certificate", url: data?.registrationCert },
			{ label: "Address Proof", url: data?.addressProof },
		],
		[data],
	);

	const hasBankDetails = useMemo(() => {
		const b = data?.bankDetails || {};
		return Boolean(b.bankBusinessName || b.bankName || b.accountNumber || b.accountName || b.branchName || b.IFSCCode);
	}, [data]);

	const hasDocuments = useMemo(() => docItems.some((d) => d.url), [docItems]);

	const status = useMemo(() => {
		if (data?.isRejected) return { label: "Rejected", bg: "#fee2e2", fg: "#b91c1c" };
		if (data?.isAccepted) return { label: "Approved", bg: "#dcfce7", fg: "#166534" };
		return { label: "Pending Review", bg: "#fef3c7", fg: "#92400e" };
	}, [data]);

	if (!data) return null;

	const fullName = `${data.firstName || ""} ${data.lastName || ""}`.trim();
	const isPending = !data.isAccepted && !data.isRejected;

	return (
		<>
			{/* Overlay */}
			<div style={styles.overlay} onClick={onClose} />

			{/* Modal */}
			<div style={styles.modal}>
				{/* Header */}
				<div style={styles.header}>
					<div>
						<h5 style={{ margin: 0, color: "#0f172a" }}>Seller Application Details</h5>
						<div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "6px" }}>
							<span style={{ ...styles.statusPill, backgroundColor: status.bg, color: status.fg }}>{status.label}</span>
							{data.createdAt && <span style={{ fontSize: "12px", color: "#64748b" }}>Submitted {new Date(data.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>}
						</div>
					</div>
					<button style={styles.closeBtn} onClick={onClose}>
						<i className='bi bi-x-lg'></i>
					</button>
				</div>

				{/* Content */}
				<div style={styles.body}>
					{data.isRejected && data.rejectionReason && (
						<div style={styles.rejectionBanner}>
							<i className='bi bi-exclamation-circle-fill' style={{ color: "#dc2626", marginRight: "8px" }}></i>
							<div>
								<strong style={{ color: "#991b1b" }}>Rejection reason:</strong> <span style={{ color: "#7f1d1d" }}>{data.rejectionReason}</span>
							</div>
						</div>
					)}

					{/* Personal Info */}
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-person-fill me-2' style={{ color: "#e54a13" }}></i>
							Personal Information
						</h6>
						<div className='row'>
							<div className='col-md-3 text-center mb-3'>
								<InitialsAvatar src={data.image} name={fullName} size={100} rounded={50} style={{ margin: "0 auto", border: "3px solid #e2e8f0" }} />
							</div>
							<div className='col-md-9'>
								<div className='row'>
									<div className='col-md-6'>
										<InfoField label='Full Name' value={fullName || "-"} />
										<InfoField label='Email' value={data.email} />
										<InfoField label='Mobile' value={data.mobileNumber ? `${data.countryCode || ""} ${data.mobileNumber}` : "-"} />
									</div>
									<div className='col-md-6'>
										<InfoField label='Gender' value={data.gender} />
										{data.businessTag && <InfoField label='Business Tag' value={data.businessTag} />}
										<InfoField
											label='Instagram'
											value={
												data.instagramHandle ? (
													<a href={`https://instagram.com/${data.instagramHandle}`} target='_blank' rel='noopener noreferrer' style={{ color: "#e54a13", textDecoration: "none" }}>
														@{data.instagramHandle}
													</a>
												) : (
													"-"
												)
											}
										/>
										<InfoField
											label='TikTok'
											value={
												data.tiktokHandle ? (
													<a href={`https://tiktok.com/@${data.tiktokHandle}`} target='_blank' rel='noopener noreferrer' style={{ color: "#e54a13", textDecoration: "none" }}>
														@{data.tiktokHandle}
													</a>
												) : (
													"-"
												)
											}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Store Info */}
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-shop me-2' style={{ color: "#16a34a" }}></i>
							Store Information
						</h6>
						<div className='row'>
							<div className='col-md-3 text-center mb-3'>
								{data.logo ? (
									<img
										src={data.logo}
										alt='Store Logo'
										style={styles.logoImg}
										onClick={() => setLightboxImg(data.logo)}
										onError={(e) => {
											e.target.onerror = null;
											e.target.src = defaultImage;
										}}
									/>
								) : (
									<div style={styles.noImage}>No Logo</div>
								)}
								<small style={{ color: "#94a3b8" }}>Store Logo</small>
							</div>
							<div className='col-md-9'>
								<div className='row'>
									<div className='col-md-6'>
										<InfoField label='Store Name' value={data.storeName} />
										{data.businessName && <InfoField label='Business Name' value={data.businessName} />}
										{data.businessType && <InfoField label='Business Type' value={data.businessType} />}
									</div>
									<div className='col-md-6'>
										<InfoField label='Category' value={data.category} />
										<InfoField
											label='Instagram'
											value={
												data.instagramHandle ? (
													<a href={`https://instagram.com/${data.instagramHandle}`} target='_blank' rel='noopener noreferrer' style={{ color: "#e54a13", textDecoration: "none" }}>
														@{data.instagramHandle}
													</a>
												) : (
													"-"
												)
											}
										/>
										<InfoField
											label='TikTok'
											value={
												data.tiktokHandle ? (
													<a href={`https://tiktok.com/@${data.tiktokHandle}`} target='_blank' rel='noopener noreferrer' style={{ color: "#e54a13", textDecoration: "none" }}>
														@{data.tiktokHandle}
													</a>
												) : (
													"-"
												)
											}
										/>
										{data.description && <InfoField label='Description' value={data.description} />}
									</div>
								</div>
							</div>
						</div>
					</div>

					{/* Address Info */}
					<div style={styles.section}>
						<h6 style={styles.sectionTitle}>
							<i className='bi bi-geo-alt-fill me-2' style={{ color: "#f59e0b" }}></i>
							Address
						</h6>
						<div className='row'>
							<div className='col-md-4'>
								<InfoField label='Address' value={data.address?.address} />
							</div>
							<div className='col-md-4'>
								<InfoField label='City' value={data.address?.city} />
							</div>
							<div className='col-md-4'>
								<InfoField label='State' value={data.address?.state} />
							</div>
							<div className='col-md-4'>
								<InfoField label='Country' value={data.address?.country} />
							</div>
							{data.address?.pinCode && (
								<div className='col-md-4'>
									<InfoField label='Pin Code' value={data.address?.pinCode} />
								</div>
							)}
							{data.address?.landMark && (
								<div className='col-md-4'>
									<InfoField label='Landmark' value={data.address?.landMark} />
								</div>
							)}
						</div>
					</div>

					{/* Bank Details — only when at least one field is filled */}
					{hasBankDetails && (
						<div style={styles.section}>
							<h6 style={styles.sectionTitle}>
								<i className='bi bi-bank me-2' style={{ color: "#0ea5e9" }}></i>
								Bank Details
							</h6>
							<div className='row'>
								{data.bankDetails?.bankBusinessName && (
									<div className='col-md-4'>
										<InfoField label='Bank Business Name' value={data.bankDetails.bankBusinessName} />
									</div>
								)}
								{data.bankDetails?.bankName && (
									<div className='col-md-4'>
										<InfoField label='Bank Name' value={data.bankDetails.bankName} />
									</div>
								)}
								{data.bankDetails?.accountNumber && (
									<div className='col-md-4'>
										<InfoField label='Account Number' value={data.bankDetails.accountNumber} />
									</div>
								)}
								{data.bankDetails?.accountName && (
									<div className='col-md-4'>
										<InfoField label='Account Name' value={data.bankDetails.accountName} />
									</div>
								)}
								{data.bankDetails?.branchName && (
									<div className='col-md-4'>
										<InfoField label='Branch Name' value={data.bankDetails.branchName} />
									</div>
								)}
								{data.bankDetails?.IFSCCode && (
									<div className='col-md-4'>
										<InfoField label='IFSC / Sort Code' value={data.bankDetails.IFSCCode} />
									</div>
								)}
							</div>
						</div>
					)}

					{/* Documents — only when at least one is uploaded */}
					{hasDocuments && (
						<div style={styles.section}>
							<h6 style={styles.sectionTitle}>
								<i className='bi bi-file-earmark-text-fill me-2' style={{ color: "#ef4444" }}></i>
								Uploaded Documents
							</h6>
							<div className='row'>
								{docItems
									.filter((d) => d.url)
									.map((doc, i) => (
										<div className='col-md-4 mb-3' key={i}>
											<label style={styles.fieldLabel}>{doc.label}</label>
											<div style={styles.docCard} onClick={() => setLightboxImg(doc.url)}>
												<img
													src={doc.url}
													alt={doc.label}
													style={styles.docImg}
													onError={(e) => {
														e.target.onerror = null;
														e.target.src = defaultImage;
													}}
												/>
												<div style={styles.docOverlay}>
													<i className='bi bi-zoom-in' style={{ fontSize: "20px", color: "#fff" }}></i>
													<span style={{ color: "#fff", fontSize: "12px" }}>Click to view</span>
												</div>
											</div>
										</div>
									))}
							</div>
						</div>
					)}
				</div>

				{/* Sticky action footer */}
				{isPending && (onApprove || onReject) && (
					<div style={styles.footer}>
						<button style={styles.secondaryBtn} onClick={onClose}>
							Close
						</button>
						<div style={{ display: "flex", gap: "10px" }}>
							{onReject && (
								<button style={styles.rejectBtn} onClick={onReject}>
									<i className='bi bi-x-lg me-2'></i>Reject
								</button>
							)}
							{onApprove && (
								<button style={styles.approveBtn} onClick={onApprove}>
									<i className='bi bi-check-lg me-2'></i>Approve
								</button>
							)}
						</div>
					</div>
				)}
			</div>

			{/* Lightbox */}
			{lightboxImg && (
				<>
					<div style={styles.lightboxOverlay} onClick={() => setLightboxImg(null)} />
					<div style={styles.lightboxContainer} onClick={() => setLightboxImg(null)}>
						<img src={lightboxImg} alt='Document' style={styles.lightboxImg} />
						<button style={styles.lightboxClose} onClick={() => setLightboxImg(null)}>
							<i className='bi bi-x-lg'></i>
						</button>
					</div>
				</>
			)}
		</>
	);
};

const InfoField = ({ label, value }) => (
	<div style={{ marginBottom: "12px" }}>
		<label style={styles.fieldLabel}>{label}</label>
		<div style={styles.fieldValue}>{value || "-"}</div>
	</div>
);

const styles = {
	overlay: {
		position: "fixed",
		inset: 0,
		backgroundColor: "rgba(0,0,0,0.4)",
		zIndex: 1040,
	},
	modal: {
		position: "fixed",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: "90%",
		maxWidth: "950px",
		maxHeight: "90vh",
		backgroundColor: "#ffffff",
		borderRadius: "16px",
		zIndex: 1050,
		display: "flex",
		flexDirection: "column",
		boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
	},
	header: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "flex-start",
		padding: "16px 24px",
		borderBottom: "1px solid #e2e8f0",
	},
	statusPill: {
		padding: "3px 10px",
		borderRadius: "999px",
		fontSize: "11px",
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.5px",
	},
	rejectionBanner: {
		display: "flex",
		alignItems: "flex-start",
		padding: "12px 16px",
		borderRadius: "10px",
		backgroundColor: "#fef2f2",
		border: "1px solid #fecaca",
		marginBottom: "16px",
		fontSize: "14px",
	},
	footer: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: "14px 24px",
		borderTop: "1px solid #e2e8f0",
		backgroundColor: "#f8fafc",
		borderBottomLeftRadius: "16px",
		borderBottomRightRadius: "16px",
	},
	secondaryBtn: {
		border: "1px solid #cbd5e1",
		backgroundColor: "#fff",
		color: "#475569",
		padding: "8px 18px",
		borderRadius: "8px",
		fontWeight: 500,
		cursor: "pointer",
	},
	approveBtn: {
		border: "none",
		backgroundColor: "#16a34a",
		color: "#fff",
		padding: "8px 20px",
		borderRadius: "8px",
		fontWeight: 600,
		cursor: "pointer",
		display: "inline-flex",
		alignItems: "center",
	},
	rejectBtn: {
		border: "none",
		backgroundColor: "#dc2626",
		color: "#fff",
		padding: "8px 20px",
		borderRadius: "8px",
		fontWeight: 600,
		cursor: "pointer",
		display: "inline-flex",
		alignItems: "center",
	},
	closeBtn: {
		background: "none",
		border: "none",
		color: "#94a3b8",
		fontSize: "18px",
		cursor: "pointer",
	},
	body: {
		padding: "24px",
		overflowY: "auto",
		flex: 1,
	},
	section: {
		marginBottom: "24px",
		padding: "16px",
		backgroundColor: "#f8fafc",
		borderRadius: "12px",
		border: "1px solid #e2e8f0",
	},
	sectionTitle: {
		color: "#0f172a",
		fontWeight: 600,
		marginBottom: "16px",
		paddingBottom: "8px",
		borderBottom: "1px solid #e2e8f0",
	},
	profileImg: {
		width: "100px",
		height: "100px",
		borderRadius: "50%",
		objectFit: "cover",
		border: "3px solid #e2e8f0",
	},
	logoImg: {
		width: "80px",
		height: "80px",
		borderRadius: "8px",
		objectFit: "cover",
		cursor: "pointer",
		border: "2px solid #e2e8f0",
		transition: "border-color 0.2s",
	},
	noImage: {
		width: "80px",
		height: "80px",
		borderRadius: "8px",
		backgroundColor: "#f1f5f9",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "#94a3b8",
		fontSize: "12px",
		margin: "0 auto",
	},
	fieldLabel: {
		fontSize: "12px",
		fontWeight: 500,
		color: "#64748b",
		textTransform: "uppercase",
		letterSpacing: "0.5px",
		display: "block",
		marginBottom: "4px",
	},
	fieldValue: {
		color: "#334155",
		fontSize: "14px",
	},
	docCard: {
		position: "relative",
		borderRadius: "8px",
		overflow: "hidden",
		cursor: "pointer",
		border: "2px solid #e2e8f0",
		transition: "border-color 0.2s",
	},
	docImg: {
		width: "100%",
		height: "140px",
		objectFit: "cover",
		display: "block",
	},
	docOverlay: {
		position: "absolute",
		inset: 0,
		backgroundColor: "rgba(0,0,0,0.4)",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		opacity: 0,
		transition: "opacity 0.2s",
	},
	noDoc: {
		height: "140px",
		backgroundColor: "#f1f5f9",
		borderRadius: "8px",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		gap: "4px",
	},
	lightboxOverlay: {
		position: "fixed",
		inset: 0,
		backgroundColor: "rgba(0,0,0,0.85)",
		zIndex: 1060,
	},
	lightboxContainer: {
		position: "fixed",
		inset: 0,
		zIndex: 1070,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		padding: "40px",
	},
	lightboxImg: {
		maxWidth: "90%",
		maxHeight: "85vh",
		objectFit: "contain",
		borderRadius: "8px",
		boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
	},
	lightboxClose: {
		position: "absolute",
		top: "20px",
		right: "20px",
		background: "rgba(255,255,255,0.15)",
		border: "none",
		color: "#fff",
		width: "40px",
		height: "40px",
		borderRadius: "50%",
		fontSize: "18px",
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
};

export default SellerRequestDetail;
